import { vec3 } from "gl-matrix";
import { Camera } from "./common";
import { catmullRomCurve } from "./catmullRom";
import { containsNans } from "./math";

interface Keyframe {
  camera: Camera;
}

/**
 * A stateless camera path with multiple keyframes.
 * Uses catmull-rom interpolation.
 * Input time is assumed to be to in range [0, duration].
 * Internally keyframes are normalized to [0, 1].
 * Interpolates between up to 3 keyframes.
 */
export interface CameraPath {
  keyframes: Keyframe[];
  evaluate: (time: number) => Camera;
}

function catmullRom(t: number, p0: number, p1: number, p2: number, p3: number) {
  const t2 = t * t;
  const a0 = -0.5 * p0 + 1.5 * p1 - 1.5 * p2 + 0.5 * p3;
  const a1 = p0 - 2.5 * p1 + 2 * p2 - 0.5 * p3;
  const a2 = -0.5 * p0 + 0.5 * p2;
  const a3 = p1;

  return a0 * t * t2 + a1 * t2 + a2 * t + a3;
}

function catmullRomVec3(
  t: number,
  p0: vec3,
  p1: vec3,
  p2: vec3,
  p3: vec3,
): vec3 {
  if (vec3.equals(p1, p2)) {
    return p1;
  }
  return catmullRomCurve(p0, p1, p2, p3)(t);
}

function interpolatePoints(
  keyframes: Keyframe[],
  // Time is normalized to [0, 1]
  normalizedTime: number,
): Camera {
  // Since we are using centripetal catmull-rom, we can't just use the length of the keyframes
  const totalSegments = keyframes.length - 3;

  const fraction = normalizedTime * totalSegments;
  const currentFrameIndex = Math.trunc(fraction) + 1;
  const t = fraction - Math.trunc(fraction);

  const nextFrameIndex = currentFrameIndex + 1;
  const previousFrameIndex = currentFrameIndex - 1;
  const nextFrame2Index = currentFrameIndex + 2;

  const previousFrame = keyframes[previousFrameIndex];
  const currentFrame = keyframes[currentFrameIndex];
  const nextFrame = keyframes[nextFrameIndex];
  const nextFrame2 = keyframes[nextFrame2Index];

  const position = catmullRomVec3(
    t,
    previousFrame.camera.position,
    currentFrame.camera.position,
    nextFrame.camera.position,
    nextFrame2.camera.position,
  );

  const target = catmullRomVec3(
    t,
    previousFrame.camera.target,
    currentFrame.camera.target,
    nextFrame.camera.target,
    nextFrame2.camera.target,
  );

  const rotationY = catmullRom(
    t,
    previousFrame.camera.rotationY ?? 0,
    currentFrame.camera.rotationY ?? 0,
    nextFrame.camera.rotationY ?? 0,
    nextFrame2.camera.rotationY ?? 0,
  );

  const zoom = catmullRom(
    t,
    previousFrame.camera.zoom ?? 1,
    currentFrame.camera.zoom ?? 1,
    nextFrame.camera.zoom ?? 1,
    nextFrame2.camera.zoom ?? 1,
  );

  const roll = catmullRom(
    t,
    previousFrame.camera.roll ?? 0,
    currentFrame.camera.roll ?? 0,
    nextFrame.camera.roll ?? 0,
    nextFrame2.camera.roll ?? 0,
  );

  return {
    position,
    target,
    rotationY,
    zoom,
    roll,
  };
}

export function createCameraPath(frames: Camera[]): CameraPath {
  const keyframes: Keyframe[] = frames.map((camera) => ({
    camera,
  }));

  if (keyframes.length < 4) {
    throw new Error("At least 4 keyframes are required");
  }

  function evaluate(normalizedTime: number): Camera {
    const newCamera = interpolatePoints(keyframes, normalizedTime);
    if (containsNans(newCamera.position)) {
      console.error("Nans in camera position", newCamera);
    }
    if (containsNans(newCamera.target)) {
      console.error("Nans in camera target", newCamera);
    }
    return newCamera;
  }

  return {
    keyframes,
    evaluate,
  };
}
